import { retryWithBackoff } from '@canvas/retry';

import { AtlassianClient } from '../atlassian-client';

export interface CanvasSessionBulkTokensResponse {
  data: {
    access: {
      token: string;
    };
    media: {
      token: string;
      baseUrl: string;
      contentId: string;
    };
    expires: number;
    features: Record<string, any>;
    edition: string;
  };
  error: string | null;
  contentId: number;
}

export type CanvasSessionBulkTokensArrayResponse = CanvasSessionBulkTokensResponse[];

export interface ConfluenceRestClientOptions {
  baseUrl: string;
}

const fetcher = async (input: RequestInfo, init?: RequestInit) =>
  await retryWithBackoff({
    callback: () => globalThis.fetch(input, init),
    maxRetries: 3,
  });

export class ConfluenceRestClient {
  private atlassianClient: AtlassianClient;

  constructor(options: ConfluenceRestClientOptions) {
    this.atlassianClient = new AtlassianClient(fetcher, new URL(options.baseUrl));
  }

  /**
   * Fetches bulk session tokens for canvas content
   * @param contentIds Array of content IDs to fetch tokens for
   * @returns Promise resolving to the bulk tokens response
   * @throws Error if any token in the response contains an error
   */
  async getCanvasSessionBulkTokens(
    contentIds: string[],
  ): Promise<CanvasSessionBulkTokensArrayResponse> {
    const ids = contentIds.join(',');
    const endpoint = `/wiki/rest/internal/1.0/canvas/session/bulk?ids=${ids}`;

    const response =
      await this.atlassianClient.get<CanvasSessionBulkTokensArrayResponse>(endpoint);

    // Validate that response is an array before iterating
    if (!Array.isArray(response)) {
      const responseType = response === null ? 'null' : typeof response;
      throw new Error(
        `Canvas session bulk tokens API returned invalid response: expected array, got ${responseType}. Response: ${JSON.stringify(
          response,
        )}`,
      );
    }

    // Check for errors in any of the token responses
    for (const tokenResponse of response) {
      if (tokenResponse.error) {
        throw new Error(`Canvas session token error: ${tokenResponse.error}`);
      }
    }

    return response;
  }
}
